import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import classnames from "classnames";
import reactStringReplace from "react-string-replace";
import Input, { InputProps } from "@/components/Input";
import KeyboardAccessibleList from "@/components/KeyboardAccessibleList";
import Spinner from "@/components/Spinner";
import styles from "./styles.module.scss";
import { KeyEnum } from "@/components/KeyboardAccessibleDiv";
import { OptionType } from "@/services/type";
interface BaseAutoCompletePropsType<T> extends Omit<InputProps, "onChange" | "value"> {
  options: OptionType<T>[];

  /**
   * display loading indicator on right of input alongside a message on dropdown list
   */
  loading?: boolean;

  /**
   * By default, when there is no term on the input, dropdown is disabled
   * Sometimes need to display a default list when input is empty
   * @default false
   */
  showOptionsOnEmptyInput?: boolean;

  /**
   * maximum number of list items to display [performance boost]
   */
  threshold?: number;

  /**
   * execute whenever a user type on input
   */
  onSearch?(query: string): void;

  /**
   * triggered when a user selects an option from list
   * @param option
   */
  onChange(option: OptionType<T>): void;

  /**
   * Any option that pass the function test will be existed in a dropdown list
   * @param option
   * @param query
   */
  filterOption?(option: OptionType<T>, query: string): boolean;

  /**
   * render each `ul`
   * @default use `reactStringReplace` for bolding `query` within `label`
   * @param option
   * @param query
   */
  renderItem?(option: OptionType<T>, query: string): React.ReactNode;
}
interface SelectMode<T> {
  /**
   * In `select` mode, user have to select among given choices
   */
  mode: "select";

  /**
   * unique key of an already selected option
   */
  value?: T;
}
interface InputMode {
  /**
   * In `input` mode, user can type freely
   */
  mode: "input";

  /**
   * unique key of already selected option, in another word `OptionType["value"]`
   */
  value?: string;
}
type UnionMode<T> = SelectMode<T> | InputMode;
export type AutoCompletePropsType<T> = BaseAutoCompletePropsType<T> & UnionMode<T>;

/**
 * the generic `T` indicate a type of unique key of each option, in other words `OptionType<T>`
 */
export default function AutoComplete(t0) {
  const $ = _c(67);
  let filterOption;
  let mode;
  let onChange;
  let onSearch;
  let options;
  let otherProps;
  let renderItem;
  let t1;
  let t2;
  let threshold;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      options,
      onChange,
      threshold,
      onSearch,
      mode,
      filterOption,
      loading: t1,
      renderItem,
      showOptionsOnEmptyInput: t2,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = filterOption;
    $[2] = mode;
    $[3] = onChange;
    $[4] = onSearch;
    $[5] = options;
    $[6] = otherProps;
    $[7] = renderItem;
    $[8] = t1;
    $[9] = t2;
    $[10] = threshold;
    $[11] = value;
  } else {
    filterOption = $[1];
    mode = $[2];
    onChange = $[3];
    onSearch = $[4];
    options = $[5];
    otherProps = $[6];
    renderItem = $[7];
    t1 = $[8];
    t2 = $[9];
    threshold = $[10];
    value = $[11];
  }
  const loading = t1 === undefined ? false : t1;
  const showOptionsOnEmptyInput = t2 === undefined ? false : t2;
  const [isOpened, setIsOpened] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [selectedOption, setSelectedOption] = React.useState(null);
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const [filteredOptions, setFilteredOptions] = React.useState(t3);
  let t4;
  if ($[13] !== onChange) {
    t4 = option => {
      onChange(option);
      setIsOpened(false);
    };
    $[13] = onChange;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const onSelect = t4;
  let t5;
  let t6;
  if ($[15] !== mode || $[16] !== options || $[17] !== value) {
    t5 = () => {
      const foundedOption = options.find(option_0 => option_0.value === value);
      if (foundedOption) {
        setQuery(foundedOption.label);
        setSelectedOption(foundedOption);
      } else {
        if (mode === "select") {
          setQuery("");
        } else {
          setQuery(value ?? "");
        }
        setSelectedOption(null);
      }
    };
    t6 = [mode, options, value];
    $[15] = mode;
    $[16] = options;
    $[17] = value;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  React.useEffect(t5, t6);
  let t7;
  let t8;
  if ($[20] !== filterOption || $[21] !== options || $[22] !== query || $[23] !== threshold) {
    t7 = () => {
      const maximumCountOfFilteredOptions = threshold && threshold < options.length ? threshold : options.length;
      if (query) {
        const newOptions = [];
        for (let index = 0; index < options.length && newOptions.length < maximumCountOfFilteredOptions; index++) {
          if (filterOption) {
            if (filterOption?.(options[index], query)) {
              newOptions.push(options[index]);
            }
          } else {
            newOptions.push(options[index]);
          }
        }
        setFilteredOptions(newOptions);
      } else {
        const newOptions_0 = options.slice(0, maximumCountOfFilteredOptions);
        setFilteredOptions(newOptions_0);
      }
    };
    t8 = [filterOption, options, query, threshold];
    $[20] = filterOption;
    $[21] = options;
    $[22] = query;
    $[23] = threshold;
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  React.useEffect(t7, t8);
  let t10;
  let t9;
  if ($[26] !== isFocused || $[27] !== isOpened || $[28] !== mode || $[29] !== selectedOption) {
    t9 = () => {
      if (!isOpened && !isFocused && mode === "select") {
        setQuery(selectedOption ? selectedOption.label : "");
      }
    };
    t10 = [isFocused, isOpened, mode, selectedOption];
    $[26] = isFocused;
    $[27] = isOpened;
    $[28] = mode;
    $[29] = selectedOption;
    $[30] = t10;
    $[31] = t9;
  } else {
    t10 = $[30];
    t9 = $[31];
  }
  React.useEffect(t9, t10);
  let t11;
  if ($[32] !== loading) {
    t11 = classnames({
      [styles.hasLoading]: loading
    });
    $[32] = loading;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== onSearch) {
    t12 = value_0 => {
      if (onSearch) {
        onSearch(value_0);
      }
      setQuery(value_0);
    };
    $[34] = onSearch;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  let t14;
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setIsFocused(false);
      setIsOpened(false);
    };
    t14 = () => {
      setIsFocused(true);
      setIsOpened(true);
    };
    t15 = () => {
      setIsOpened(_temp);
    };
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t13 = $[36];
    t14 = $[37];
    t15 = $[38];
  }
  let t16;
  if ($[39] !== isFocused || $[40] !== isOpened) {
    t16 = event => {
      if (isFocused && !isOpened && event.key !== KeyEnum.escape) {
        event.stopPropagation();
        setIsOpened(true);
      }
    };
    $[39] = isFocused;
    $[40] = isOpened;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== otherProps || $[43] !== query || $[44] !== t11 || $[45] !== t12 || $[46] !== t16) {
    t17 = <Input className={t11} {...otherProps} value={query} onChange={t12} onBlur={t13} onFocus={t14} onMouseDown={t15} onKeyDown={t16} />;
    $[42] = otherProps;
    $[43] = query;
    $[44] = t11;
    $[45] = t12;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== loading) {
    t18 = loading ? <Spinner className={styles.spinner} /> : null;
    $[48] = loading;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  const t19 = isOpened && (showOptionsOnEmptyInput ? true : query.length > 0);
  let t20;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setIsOpened(false);
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== selectedOption) {
    t21 = selectedOption ? [selectedOption.value] : [];
    $[51] = selectedOption;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== query || $[54] !== renderItem) {
    t22 = option_1 => renderItem ? renderItem(option_1, query) : reactStringReplace(option_1.label, query, _temp2);
    $[53] = query;
    $[54] = renderItem;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== filteredOptions || $[57] !== loading || $[58] !== onSelect || $[59] !== t19 || $[60] !== t21 || $[61] !== t22) {
    t23 = <KeyboardAccessibleList isEnable={t19} options={filteredOptions} onEscape={t20} isLoading={loading} selectedValues={t21} onClick={onSelect} onEnter={onSelect} containerClasses="dropDownList" renderItem={t22} />;
    $[56] = filteredOptions;
    $[57] = loading;
    $[58] = onSelect;
    $[59] = t19;
    $[60] = t21;
    $[61] = t22;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== t17 || $[64] !== t18 || $[65] !== t23) {
    t24 = <div className="flex relative flex-col">{t17}{t18}{t23}</div>;
    $[63] = t17;
    $[64] = t18;
    $[65] = t23;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  return t24;
}
function _temp2(match, i) {
  return <strong key={i}>{match}</strong>;
}
function _temp(prevState) {
  return !prevState;
}